import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Select, Button, Card, Row, Col, message, InputNumber, Switch, Tooltip, Slider, Spin } from 'antd';
import { SaveOutlined, CloseOutlined, QuestionCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { handleFormSubmitError } from '../../utils/errorHandler';
import { dealsApi, leadSettingsApi, clientCategoriesApi, clientsApi } from '../../utils/api';
import {
  parseDealCreateResponse,
  mapDealCreateToFilterOptions,
  mergeLeadTagsIntoCatalog,
  resolveCreateFormLeadNote,
} from '../../utils/dealCreateFormUtils';
import PersianDatePicker from '../PersianDatePicker';
import PriorityStars from '../common/PriorityStars';
import PhoneDuplicateAlert from '../common/PhoneDuplicateAlert';
import { usePhoneDuplicateCheck } from '../../hooks/usePhoneDuplicateCheck';
import { useAuth } from '../../contexts/AuthContext';
import useRemoteLeadOptions from '../../hooks/useRemoteLeadOptions';
import RemoteSelect from '../common/RemoteSelect';

const { Option } = Select;

const flattenLeadCategories = (payload) => {
  const result = [];
  const walk = (items, prefix = '') => {
    (items || []).forEach((item) => {
      const name = item.category_name || item.name;
      if (!name || item.id == null) return;
      result.push({ id: item.id, name: prefix ? `${prefix} / ${name}` : name });
      if (item.children?.length) walk(item.children, name);
    });
  };
  const data = payload?.data || payload || {};
  if (data.parent_categories) walk(data.parent_categories);
  else if (Array.isArray(data)) walk(data);
  else if (Array.isArray(data.categories)) walk(data.categories);
  return result;
};

const DealForm = ({
  initialValues = null,
  createFormBootstrap = null,
  presetLeadOption = null,
  presetClientOption = null,
  lockLeadContact = false,
  lockClientContact = false,
  existingContactSource = 'client',
  onSave,
  onCancel,
  loading = false,
  isEdit = false,
}) => {
  const [form] = Form.useForm();
  const { user } = useAuth();
  const isClientLocked = lockClientContact || Boolean(presetClientOption?.id);
  const {
    checkPhone,
    clearDuplicate,
    duplicateInfo,
    loading: phoneCheckLoading,
    hasDuplicate,
  } = usePhoneDuplicateCheck();
  const isLeadLocked = lockLeadContact || Boolean(presetLeadOption?.id);
  const [hasExistingLead, setHasExistingLead] = useState(
    () => Boolean(isLeadLocked || isClientLocked || presetLeadOption?.id || presetClientOption?.id),
  );
  const [filterOptions, setFilterOptions] = useState({
    pipelines: [], // شامل stages هر pipeline
    categories: [],
    products: [],
    owners: [],
    stages: [],
    leads: [],
    clients: [],
    sources: [],
    leadCategories: [],
    clientCategories: [],
    tags: [],
  });
  const [loadingOptions, setLoadingOptions] = useState(true);
  // سرنخ‌ها با جستجو و صفحه‌بندی سمت سرور (deals/lead-options) — نه کل لیست
  const remoteLeads = useRemoteLeadOptions();
  const leadSelectOptions = useMemo(() => {
    const seen = new Set();
    const merged = [];
    filterOptions.leads.forEach((l) => {
      const id = Number(l.id);
      if (seen.has(id)) return;
      seen.add(id);
      merged.push({ id, name: l.name });
    });
    remoteLeads.options.forEach((o) => {
      if (seen.has(o.value)) return;
      seen.add(o.value);
      merged.push({ id: o.value, name: o.mobile ? `${o.label} (${o.mobile})` : o.label });
    });
    return merged;
  }, [filterOptions.leads, remoteLeads.options]);
  const [loadingClients, setLoadingClients] = useState(false);
  const clientsFetchedRef = React.useRef(false);
  // جستجوی سمت سرور مشتری بر اساس نام یا شماره موبایل
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const clientSearchTimerRef = React.useRef(null);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [availableStages, setAvailableStages] = useState([]);
  const [loadingStages] = useState(false);
  const [allPipelinesWithStages, setAllPipelinesWithStages] = useState([]);
  const isLeadLockedMode = isLeadLocked && !isClientLocked;
  const useClientPicker = !isLeadLockedMode;
  const showNewClientFields = !hasExistingLead && !isLeadLockedMode && !isClientLocked;
  const blockSubmit = showNewClientFields && hasDuplicate;

  const applyCreateFormNote = React.useCallback(
    (bootstrap, extraValues = null) => {
      const data = parseDealCreateResponse(bootstrap);
      const leadNote = resolveCreateFormLeadNote(data, extraValues?.note);
      const patch = { ...(extraValues || {}) };
      if (leadNote) patch.note = leadNote;
      if (Object.keys(patch).length) form.setFieldsValue(patch);
    },
    [form],
  );

  // بارگذاری از GET /deals/create (یک درخواست واحد)
  useEffect(() => {
    if (!createFormBootstrap) return;

    const applyBootstrap = async () => {
      setLoadingOptions(true);
      let bootstrapData = null;
      try {
        bootstrapData = parseDealCreateResponse(createFormBootstrap);
        const mapped = mapDealCreateToFilterOptions(bootstrapData);
        const mergedTags = mergeLeadTagsIntoCatalog(mapped.tags, bootstrapData.lead?.tags);

        setAllPipelinesWithStages(mapped.pipelinesWithStages);
        setFilterOptions({
          pipelines: mapped.pipelines,
          categories: mapped.categories,
          products: mapped.products,
          owners: mapped.owners,
          stages: mapped.stages,
          leads: mapped.leads,
          clients: mapped.clients,
          sources: mapped.sources,
          leadCategories: mapped.leadCategories,
          clientCategories: mapped.clientCategories,
          tags: mergedTags,
        });

        if (bootstrapData.default_pipeline_id) {
          setSelectedPipeline(Number(bootstrapData.default_pipeline_id));
          setAvailableStages(mapped.stages);
        }
        clientsFetchedRef.current = true;

        if (!mapped.clientCategories.length) {
          try {
            const ccRes = await clientCategoriesApi.getCreateData();
            const ccData = ccRes?.data ?? ccRes ?? {};
            const clientCategoriesList = (ccData.categories || ccData.client_categories || []).map((c) => ({
              id: c.id,
              name: c.category_name || c.name,
            }));
            if (clientCategoriesList.length) {
              setFilterOptions((prev) => ({ ...prev, clientCategories: clientCategoriesList }));
            }
          } catch (err) {
            console.warn('Could not fetch client categories:', err);
          }
        }
      } catch (error) {
        console.error('خطا در پردازش داده فرم ایجاد معامله:', error);
        message.error('خطا در بارگذاری اطلاعات فرم');
      } finally {
        setLoadingOptions(false);
        if (bootstrapData) {
          applyCreateFormNote(createFormBootstrap);
        }
      }
    };

    applyBootstrap();
  }, [createFormBootstrap, applyCreateFormNote]);

  // پس از بارگذاری گزینه‌ها، مقادیر اولیه (مثل تگ‌ها) را اعمال کن
  useEffect(() => {
    if (!createFormBootstrap || loadingOptions || !initialValues) return;
    applyCreateFormNote(createFormBootstrap, initialValues);
    if (initialValues.has_lead_contact !== undefined) {
      setHasExistingLead(initialValues.has_lead_contact);
    }
    if (initialValues.pipeline && allPipelinesWithStages.length) {
      const pipelineId = Number(initialValues.pipeline);
      const matchedPipeline = allPipelinesWithStages.find(
        (pipeline) => Number(pipeline.id) === pipelineId,
      );
      if (matchedPipeline) {
        setSelectedPipeline(pipelineId);
        const stagesData = (matchedPipeline.stages || [])
          .map((stage) => ({
            id: Number(stage.id),
            name: stage.name,
            label_color: stage.label_color || '#1890ff',
            priority: stage.priority || 0,
          }))
          .sort((a, b) => (a.priority || 0) - (b.priority || 0));
        setAvailableStages(stagesData);
        setFilterOptions((prev) => ({ ...prev, stages: stagesData }));
      }
    }
  }, [createFormBootstrap, loadingOptions, initialValues, form, allPipelinesWithStages, applyCreateFormNote]);

  // بارگذاری داده‌ها از API (حالت ویرایش یا fallback)
  useEffect(() => {
    if (createFormBootstrap) return;

    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        // دریافت فیلترهای deals
        const dealsRes = await dealsApi.getAll({
          pipeline_id: 'all',
          category_id: 'all',
          product_id: 'all',
          deal_owner_id: 'all',
          stage_id: 'all',
          lead_id: 'all',
          source_id: 'all',
          tag: 'all',
          page: 1,
          per_page: 1,
        });

        const apiFilters = dealsRes?.filters || {};

        // دریافت pipelines با stages از lead-pipeline-settings
        let pipelinesWithStages = [];
        try {
          const pipelinesRes = await leadSettingsApi.getPipelines();
          pipelinesWithStages = pipelinesRes?.data?.pipelines || [];
          console.log('📥 DealForm - Pipelines with stages:', pipelinesWithStages);
          setAllPipelinesWithStages(pipelinesWithStages);
        } catch (err) {
          console.warn('Could not fetch pipelines with stages:', err);
          // اگر API جدید کار نکرد، از pipelines موجود استفاده کن
          pipelinesWithStages = (apiFilters.pipelines || []).map(p => ({ ...p, stages: [] }));
          setAllPipelinesWithStages(pipelinesWithStages);
        }

        // محصولات / تگ‌ها / شیوه‌های آشنایی را RemoteSelect خودش از options/* جستجو می‌کند
        const productsData = [];

        // دسته‌بندی سرنخ
        let leadCategoriesList = (apiFilters.lead_categories || []).map((c) => ({
          id: c.id,
          name: c.category_name || c.name,
        }));
        if (!leadCategoriesList.length) {
          try {
            const lcRes = await leadSettingsApi.getLeadCategories('lead');
            leadCategoriesList = flattenLeadCategories(lcRes?.data || lcRes);
          } catch (err) {
            console.warn('Could not fetch lead categories:', err);
          }
        }

        let clientCategoriesList = [];
        try {
          const createRes = await dealsApi.getCreateForm();
          const createData = createRes?.data ?? createRes ?? {};
          clientCategoriesList = (createData.client_categories || createData.clientCategories || createData.categories || [])
            .map((c) => ({ id: c.id, name: c.category_name || c.name }));
        } catch (err) {
          console.warn('Could not fetch deals create form:', err);
        }
        if (!clientCategoriesList.length) {
          try {
            const ccRes = await clientCategoriesApi.getCreateData();
            const ccData = ccRes?.data ?? ccRes ?? {};
            clientCategoriesList = (ccData.categories || ccData.client_categories || []).map((c) => ({
              id: c.id,
              name: c.category_name || c.name,
            }));
          } catch (err) {
            console.warn('Could not fetch client categories:', err);
          }
        }

        setFilterOptions({
          pipelines: pipelinesWithStages.map(p => ({ id: p.id, name: p.name, stages: p.stages || [] })),
          categories: (apiFilters.categories || []).map(c => ({ id: c.id, name: c.category_name || c.name })),
          products: productsData,
          owners: (apiFilters.dealWatcher || apiFilters.employees || []).map(o => ({ id: o.id, name: o.name })),
          stages: [],
          leads: [],
          clients: [],
          sources: [],
          leadCategories: leadCategoriesList,
          clientCategories: clientCategoriesList,
          tags: [],
        });

        console.log('📥 DealForm - FilterOptions set');
      } catch (error) {
        console.error('خطا در دریافت گزینه‌ها:', error);
        message.error('خطا در دریافت اطلاعات');
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [useClientPicker, isLeadLockedMode, createFormBootstrap]);

  useEffect(() => {
    // مشتریان فقط از GET /deals/create (createFormBootstrap) — هرگز /clients index
    if (createFormBootstrap) return;
    if (!hasExistingLead || !useClientPicker || isLeadLockedMode || isClientLocked || clientsFetchedRef.current) {
      return;
    }

    let cancelled = false;
    setLoadingClients(true);

    const loadClients = async () => {
      try {
        // فقط صفحه اول؛ بقیه با جستجو (clients/options)
        const clientsRes = await clientsApi.getOptions({ per_page: 50, include_inactive: true });
        if (cancelled) return;

        const clientsList = (clientsRes?.data || []).map((c) => ({
          id: c.id,
          name: c.name || c.company_name || `مشتری #${c.id}`,
          mobile: c.mobile || '',
        }));

        setFilterOptions((prev) => ({ ...prev, clients: clientsList }));
        clientsFetchedRef.current = true;
      } catch (err) {
        if (cancelled) return;
        console.warn('Could not fetch clients:', err);
        message.warning('خطا در دریافت لیست مشتریان');
      } finally {
        if (!cancelled) {
          setLoadingClients(false);
        }
      }
    };

    loadClients();

    return () => {
      cancelled = true;
    };
  }, [createFormBootstrap, hasExistingLead, useClientPicker, isLeadLockedMode, isClientLocked]);

  useEffect(() => {
    if (!presetLeadOption?.id || useClientPicker) return;
    setFilterOptions((prev) => {
      if (prev.leads.some((l) => l.id === presetLeadOption.id)) return prev;
      return {
        ...prev,
        leads: [
          {
            id: presetLeadOption.id,
            name: presetLeadOption.name || `سرنخ #${presetLeadOption.id}`,
          },
          ...prev.leads,
        ],
      };
    });
  }, [presetLeadOption, loadingOptions, useClientPicker]);

  useEffect(() => {
    if (!presetClientOption?.id || !useClientPicker) return;
    setFilterOptions((prev) => {
      if (prev.clients.some((c) => c.id === presetClientOption.id)) return prev;
      return {
        ...prev,
        clients: [
          {
            id: presetClientOption.id,
            name: presetClientOption.name || `مشتری #${presetClientOption.id}`,
          },
          ...prev.clients,
        ],
      };
    });
  }, [presetClientOption, loadingOptions, useClientPicker]);

  // تنظیم مقادیر اولیه فرم
  useEffect(() => {
    if (!initialValues) return;
    if (createFormBootstrap) {
      applyCreateFormNote(createFormBootstrap, initialValues);
    } else {
      form.setFieldsValue(initialValues);
    }
    if (initialValues.has_lead_contact !== undefined) {
      setHasExistingLead(initialValues.has_lead_contact);
    }
  }, [initialValues, form, createFormBootstrap, applyCreateFormNote]);

  useEffect(() => {
    if (isEdit || !user?.id) return;
    const values = form.getFieldsValue();
    const patch = {};
    if (!values.deal_owner) patch.deal_owner = user.id;
    if (!values.added_by) patch.added_by = user.id;
    if (Object.keys(patch).length) form.setFieldsValue(patch);
  }, [form, isEdit, user?.id]);

  useEffect(() => {
    if (presetClientOption?.id && initialValues?.lead_contact) {
      setHasExistingLead(true);
      form.setFieldsValue({
        has_lead_contact: true,
        lead_contact: presetClientOption.id,
      });
    } else if (isClientLocked && presetClientOption?.id) {
      setHasExistingLead(true);
      form.setFieldsValue({
        has_lead_contact: true,
        lead_contact: presetClientOption.id,
      });
    } else if (presetLeadOption?.id && initialValues?.lead_contact) {
      setHasExistingLead(true);
      form.setFieldsValue({
        has_lead_contact: true,
        lead_contact: presetLeadOption.id,
      });
    } else if (isLeadLockedMode && presetLeadOption?.id) {
      setHasExistingLead(true);
      form.setFieldsValue({
        has_lead_contact: true,
        lead_contact: presetLeadOption.id,
      });
    }
  }, [presetLeadOption, presetClientOption, initialValues, form, isLeadLockedMode, isClientLocked]);

  // در حالت edit، وقتی pipelines لود شدند و initialValues داریم، stages مربوطه را تنظیم کن
  useEffect(() => {
    if (initialValues?.pipeline_id && allPipelinesWithStages.length > 0) {
      const pipelineId = initialValues.pipeline_id;
      console.log('📥 Setting initial pipeline stages for:', pipelineId);

      setSelectedPipeline(pipelineId);

      const selectedPipelineData = allPipelinesWithStages.find(p => p.id === pipelineId);

      if (selectedPipelineData && selectedPipelineData.stages) {
        const stagesData = selectedPipelineData.stages.map(s => ({
          id: Number(s.id),
          name: s.name,
          label_color: s.label_color || '#1890ff',
          priority: s.priority || 0,
        })).sort((a, b) => (a.priority || 0) - (b.priority || 0));

        console.log('📥 Initial stages:', stagesData);

        setAvailableStages(stagesData);
        setFilterOptions(prev => ({
          ...prev,
          stages: stagesData,
        }));
      }
    }
  }, [initialValues, allPipelinesWithStages]);

  useEffect(() => {
    const presetStageId = initialValues?.stage_id;
    if (!presetStageId || allPipelinesWithStages.length === 0 || selectedPipeline) return;
    const matchedPipeline = allPipelinesWithStages.find((pipeline) =>
      (pipeline.stages || []).some((stage) => String(stage.id) === String(presetStageId)),
    );
    if (!matchedPipeline) return;

    const stagesData = (matchedPipeline.stages || [])
      .map((stage) => ({
        id: Number(stage.id),
        name: stage.name,
        label_color: stage.label_color || '#1890ff',
        priority: stage.priority || 0,
      }))
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));

    setSelectedPipeline(matchedPipeline.id);
    setAvailableStages(stagesData);
    setFilterOptions((prev) => ({ ...prev, stages: stagesData }));
    form.setFieldsValue({
      pipeline: matchedPipeline.id,
      stage_id: Number(presetStageId),
    });
  }, [allPipelinesWithStages, form, initialValues?.stage_id, selectedPipeline]);

  // جستجوی مشتری از وب‌سرویس بر اساس نام یا شماره موبایل (با debounce)
  const fetchClientsByQuery = React.useCallback((query) => {
    if (clientSearchTimerRef.current) {
      clearTimeout(clientSearchTimerRef.current);
    }
    const q = String(query || '').trim();
    setClientSearchLoading(true);
    clientSearchTimerRef.current = setTimeout(async () => {
      try {
        // clients/options: سبک، صفحه‌بندی‌شده؛ با q خالی صفحه اول را می‌دهد
        const res = await clientsApi.getOptions({ search: q, per_page: 30, include_inactive: true });
        const list = (res?.data || []).map((c) => ({
          id: c.id,
          name: c.name || c.company_name || `مشتری #${c.id}`,
          mobile: c.mobile || '',
        }));
        setClientSearchResults(list);
      } catch (err) {
        console.warn('client search failed:', err);
        setClientSearchResults([]);
      } finally {
        setClientSearchLoading(false);
      }
    }, 350);
  }, []);

  useEffect(() => () => {
    if (clientSearchTimerRef.current) clearTimeout(clientSearchTimerRef.current);
  }, []);

  // گزینه‌های نمایش انتخاب مشتری: نتایج جستجو یا لیست اولیه + حفظ مشتری انتخاب‌شده
  const clientSelectOptions = useMemo(() => {
    const base = clientSearchResults.length ? clientSearchResults : filterOptions.clients;
    const map = new Map();
    base.forEach((c) => {
      if (c?.id != null) map.set(c.id, c);
    });
    if (presetClientOption?.id && !map.has(presetClientOption.id)) {
      map.set(presetClientOption.id, {
        id: presetClientOption.id,
        name: presetClientOption.name || `مشتری #${presetClientOption.id}`,
        mobile: presetClientOption.mobile || '',
      });
    }
    return [...map.values()];
  }, [clientSearchResults, filterOptions.clients, presetClientOption]);

  const buildClientOptionLabel = (item) =>
    item?.mobile ? `${item.name} — ${item.mobile}` : item?.name;

  // وقتی pipeline تغییر می‌کند، stages مربوط به آن را از داده‌های ذخیره شده فیلتر کن
  const handlePipelineChange = (pipelineId) => {
    setSelectedPipeline(pipelineId);
    form.setFieldsValue({ stage_id: undefined });

    if (!pipelineId) {
      setAvailableStages([]);
      return;
    }

    console.log('📥 Pipeline selected:', pipelineId);
    console.log('📥 All pipelines with stages:', allPipelinesWithStages);

    // پیدا کردن pipeline انتخاب شده از داده‌های ذخیره شده
    const selectedPipelineData = allPipelinesWithStages.find(p => p.id === pipelineId);

    if (selectedPipelineData && selectedPipelineData.stages) {
      const stagesData = selectedPipelineData.stages.map(s => ({
        id: Number(s.id),
        name: s.name,
        label_color: s.label_color || '#1890ff',
        priority: s.priority || 0,
      })).sort((a, b) => (a.priority || 0) - (b.priority || 0));

      console.log('📥 Stages for pipeline:', stagesData);

      setAvailableStages(stagesData);

      // به‌روزرسانی filterOptions.stages
      setFilterOptions(prev => ({
        ...prev,
        stages: stagesData,
      }));
    } else {
      console.warn('📥 No stages found for pipeline:', pipelineId);
      setAvailableStages([]);
    }
  };

  const handleSubmit = async (values) => {
    if (onSave) {
      await onSave(values);
    }
  };

  const handleFormFinish = async (values) => {
    try {
      if (blockSubmit) {
        message.warning('شماره واردشده تکراری است؛ ابتدا پرونده موجود را بررسی کنید');
        return;
      }
      await handleSubmit(values);
    } catch (error) {
      handleFormSubmitError(form, error);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues || undefined}
      onFinish={handleFormFinish}
      scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
      className="space-y-6"
      style={{ fontFamily: 'CustomFont' }}
    >
      {/* انتخاب نوع: با سرنخ موجود یا جدید */}
      {!isLeadLockedMode && !isClientLocked && (
        <Card
          title={<span style={{ fontFamily: 'CustomFont' }}>نوع معامله</span>}
          className="mb-6"
        >
          <Form.Item
            label="استفاده از مشتری‌های موجود"
            name="has_lead_contact"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch
              checked={hasExistingLead}
              checkedChildren="بله"
              unCheckedChildren="خیر"
              onChange={(checked) => {
                setHasExistingLead(checked);
                clearDuplicate();
                form.setFieldsValue({ has_lead_contact: checked });
                if (checked) {
                  form.setFieldsValue({
                    client_name: undefined,
                    mobile: undefined,
                    email: undefined,
                    client_category_id: undefined,
                    added_by: undefined,
                  });
                } else {
                  form.setFieldsValue({ lead_contact: undefined });
                  if (user?.id) {
                    form.setFieldsValue({ added_by: user.id, deal_owner: user.id });
                  }
                }
              }}
            />
          </Form.Item>
        </Card>
      )}

      {isLeadLockedMode && (
        <Card
          title={<span style={{ fontFamily: 'CustomFont' }}>سرنخ مرتبط</span>}
          className="mb-6"
        >
          <Form.Item name="has_lead_contact" hidden initialValue={true}>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="lead_contact" hidden rules={[{ required: true }]}>
            <Input type="hidden" />
          </Form.Item>
          <div
            className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800"
            style={{ fontFamily: 'CustomFont' }}
          >
            <span className="text-sm text-gray-500 dark:text-gray-400">سرنخ:</span>
            <span className="font-semibold text-gray-800 dark:text-gray-100">
              {presetLeadOption?.name || `سرنخ #${presetLeadOption?.id}`}
            </span>
          </div>
        </Card>
      )}

      {isClientLocked && (
        <Card
          title={<span style={{ fontFamily: 'CustomFont' }}>مشتری مرتبط</span>}
          className="mb-6"
        >
          <Form.Item name="has_lead_contact" hidden initialValue={true}>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="lead_contact" hidden rules={[{ required: true }]}>
            <Input type="hidden" />
          </Form.Item>
          <div
            className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800"
            style={{ fontFamily: 'CustomFont' }}
          >
            <span className="text-sm text-gray-500 dark:text-gray-400">مشتری:</span>
            <span className="font-semibold text-gray-800 dark:text-gray-100">
              {presetClientOption?.name || `مشتری #${presetClientOption?.id}`}
            </span>
          </div>
        </Card>
      )}

      {/* بخش اطلاعات سرنخ */}
      {hasExistingLead && !isLeadLockedMode && !isClientLocked ? (
        <Card
          title={
            <span style={{ fontFamily: 'CustomFont' }}>
              {useClientPicker ? 'انتخاب مشتری موجود' : 'انتخاب سرنخ موجود'}
            </span>
          }
          className="mb-6"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={useClientPicker ? 'مشتری' : 'سرنخ'}
                name="lead_contact"
                rules={[
                  {
                    required: true,
                    message: useClientPicker ? 'لطفاً مشتری را انتخاب کنید' : 'لطفاً سرنخ را انتخاب کنید',
                  },
                ]}
              >
                <Select
                  placeholder={
                    useClientPicker
                      ? 'جستجوی نام یا شماره موبایل مشتری'
                      : 'انتخاب سرنخ'
                  }
                  style={{ fontFamily: 'CustomFont' }}
                  loading={
                    useClientPicker
                      ? (clientSearchLoading || loadingClients || (createFormBootstrap ? loadingOptions : false))
                      : (loadingOptions || remoteLeads.loading)
                  }
                  showSearch
                  allowClear
                  optionLabelProp="label"
                  optionFilterProp="label"
                  onSearch={useClientPicker ? fetchClientsByQuery : remoteLeads.selectProps.onSearch}
                  onPopupScroll={useClientPicker ? undefined : remoteLeads.selectProps.onPopupScroll}
                  onOpenChange={
                    useClientPicker
                      ? (open) => {
                        if (open && !clientSearchResults.length) fetchClientsByQuery('');
                      }
                      : remoteLeads.selectProps.onOpenChange
                  }
                  suffixIcon={
                    useClientPicker && clientSearchLoading ? (
                      <LoadingOutlined spin />
                    ) : undefined
                  }
                  filterOption={false}
                  notFoundContent={
                    useClientPicker
                      ? (clientSearchLoading ? (
                        <div className="flex items-center justify-center gap-2 py-2 text-gray-500" style={{ fontFamily: 'CustomFont' }}>
                          <Spin size="small" />
                          <span>در حال جستجو...</span>
                        </div>
                      ) : 'برای جستجو نام یا موبایل را تایپ کنید')
                      : (remoteLeads.loading ? 'در حال جستجو...' : 'سرنخی یافت نشد')
                  }
                >
                  {useClientPicker
                    ? clientSelectOptions.map((item) => {
                      const label = buildClientOptionLabel(item);
                      return (
                        <Option key={item.id} value={item.id} label={label}>
                          {label}
                        </Option>
                      );
                    })
                    : leadSelectOptions.map((item) => (
                      <Option key={item.id} value={item.id} label={item.name}>
                        {item.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>
      ) : !isLeadLockedMode && !isClientLocked ? (
        <Card
          title={<span style={{ fontFamily: 'CustomFont' }}>اطلاعات مشتری جدید</span>}
          className="mb-6"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="نام مشتری"
                name="client_name"
                rules={[{ required: true, message: 'لطفاً نام مشتری را وارد کنید' }]}
              >
                <Input placeholder="نام مشتری" style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="موبایل"
                name="mobile"
                rules={[{ required: true, message: 'لطفاً موبایل را وارد کنید' }]}
                help={
                  <PhoneDuplicateAlert info={duplicateInfo} loading={phoneCheckLoading} />
                }
                validateStatus={hasDuplicate ? 'warning' : undefined}
              >
                <Input
                  placeholder="مثلاً: 09121212121"
                  style={{ fontFamily: 'CustomFont' }}
                  onChange={(e) => checkPhone(e.target.value)}
                  onBlur={() => {
                    if (!form.getFieldValue('mobile')) clearDuplicate();
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="ایمیل"
                name="email"
              >
                <Input type="email" placeholder="email@example.com" style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="دسته‌بندی مشتری"
                name="client_category_id"
              >
                <Select
                  allowClear
                  placeholder="انتخاب دسته‌بندی"
                  style={{ fontFamily: 'CustomFont' }}
                  loading={loadingOptions}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                >
                  {filterOptions.clientCategories.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="افزوده شده توسط"
                name="added_by"
              >
                <Select
                  allowClear
                  placeholder="انتخاب کارمند"
                  style={{ fontFamily: 'CustomFont' }}
                  loading={loadingOptions}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                >
                  {filterOptions.owners.map((o) => (
                    <Option key={o.id} value={o.id}>
                      {o.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>
      ) : null}

      {/* بخش اطلاعات معامله */}
      <Card
        title={<span style={{ fontFamily: 'CustomFont' }}>اطلاعات معامله</span>}
        className="mb-6"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="نام معامله"
              name="name"
              rules={[{ required: true, message: 'لطفاً نام معامله را وارد کنید' }]}
            >
              <Input placeholder="نام معامله" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  ارزش معامله (تومان){' '}
                  <Tooltip title="در صورتی که این معامله به موفقیت برسد و به مشتری تبدیل شود، این معامله چه مقدار واریزی خواهد داشت. مبلغ واریزی باید بر اساس توافقات اولیه و پیش‌بینی‌های شما وارد شود.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="value"
              rules={[{ required: true, message: 'لطفاً مبلغ را وارد کنید' }]}
            >
              <InputNumber
                min={0}
                placeholder="مثلاً: 1500000"
                style={{ width: '100%', fontFamily: 'CustomFont' }}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  تاریخ بسته شدن{' '}
                  <Tooltip title="تاریخ احتمالی بسته شدن معامله است که با توجه به مذاکراتی که با سرنخ داشته‌اید می‌توانید این تاریخ را حدس بزنید. این تاریخ می‌تواند به شما کمک کند تا برای پیگیری‌های بعدی برنامه‌ریزی کنید.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="close_date"
            >
              <PersianDatePicker
                placeholder="انتخاب تاریخ"
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  مالک معامله{' '}
                  <Tooltip title="مالک معامله شخصی است که مسئولیت این معامله با آن شخص است و در گزارش‌های فروش به نام مالک ثبت می‌شود. این فرد مسئول پیگیری و پیشبرد مراحل معامله است.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="deal_owner"
            >
              <Select
                placeholder="انتخاب مالک"
                style={{ fontFamily: 'CustomFont' }}
                loading={loadingOptions}
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {filterOptions.owners.map((o) => (
                  <Option key={o.id} value={o.id}>
                    {o.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  ناظر معامله{' '}
                  <Tooltip title="ناظر معامله شخصی است که در سطح بالاتر می‌تواند روی عملکرد مالک معامله نظارت کند. این فرد معمولاً برای بررسی و ارزیابی عملکرد فروشندگان و پیشرفت معاملات مسئول است.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="deal_agent_id"
            >
              <Select
                placeholder="انتخاب ناظر"
                style={{ fontFamily: 'CustomFont' }}
                loading={loadingOptions}
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {filterOptions.owners.map((o) => (
                  <Option key={o.id} value={o.id}>
                    {o.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  مسیر فروش{' '}
                  <Tooltip title="مسیر فروش برای تعیین روند و مراحل معامله است. این مسیر نشان‌دهنده مراحل مختلف از ابتدا تا انتهای معامله است که به شما کمک می‌کند تا وضعیت کنونی معامله را بهتر درک کنید.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="pipeline"
              rules={[{ required: true, message: 'لطفاً مسیر فروش را انتخاب کنید' }]}
            >
              <Select
                placeholder="انتخاب مسیر فروش"
                style={{ fontFamily: 'CustomFont' }}
                loading={loadingOptions}
                showSearch
                allowClear
                onChange={handlePipelineChange}
                optionFilterProp="children"
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {filterOptions.pipelines.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="مرحله"
              name="stage_id"
              rules={[{ required: true, message: 'لطفاً مرحله را انتخاب کنید' }]}
            >
              <Select
                placeholder={selectedPipeline ? "انتخاب مرحله" : "ابتدا مسیر فروش را انتخاب کنید"}
                style={{ fontFamily: 'CustomFont' }}
                loading={loadingStages}
                disabled={!selectedPipeline || loadingStages}
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                notFoundContent={loadingStages ? 'در حال بارگذاری...' : (availableStages.length === 0 ? 'مرحله‌ای یافت نشد' : 'مرحله‌ای یافت نشد')}
              >
                {availableStages && availableStages.length > 0 ? (
                  availableStages.map((s) => (
                    <Option key={s.id} value={Number(s.id)}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: s.label_color || '#1890ff',
                          }}
                        />
                        {s.name}
                      </span>
                    </Option>
                  ))
                ) : (
                  !loadingStages && selectedPipeline && (
                    <Option disabled value="no-stages">
                      مرحله‌ای یافت نشد
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>

          {false && (
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={
                  <span>
                    دسته‌بندی معامله{' '}
                    <Tooltip title="برای دسته‌بندی مسیرهای فروش و دسته‌بندی بهتر معاملات خود می‌توانید از این گزینه استفاده کنید. این دسته‌بندی می‌تواند بر اساس نوع مشتری یا نوع محصول باشد.">
                      <QuestionCircleOutlined className="text-gray-400" />
                    </Tooltip>
                  </span>
                }
                name="category_id"
              >
                <Select
                  placeholder="انتخاب دسته‌بندی"
                  style={{ fontFamily: 'CustomFont' }}
                  loading={loadingOptions}
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                >
                  {filterOptions.categories.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="اولویت"
              name="priority"
              initialValue={isEdit ? undefined : 1}
            >
              <PriorityStars size={22} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="احتمال موفقیت (%)"
              name="success_rate"
            >
              <Slider
                min={0}
                max={100}
                step={25}
                marks={{ 0: '0', 25: '25', 50: '50', 75: '75', 100: '100' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  محصولات{' '}
                  <Tooltip title="می‌توانید محصولات و خدمات مرتبط با این معامله را مشخص کنید. این کار باعث می‌شود تا اطلاعات معامله کامل‌تر باشد و شما بتوانید جزئیات دقیق‌تری را برای پیگیری‌ها در اختیار داشته باشید.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="product_id"
            >
              <RemoteSelect
                kind="products"
                mode="multiple"
                placeholder="انتخاب محصولات"
                knownOptions={filterOptions.products.map((o) => ({ value: o.id, label: o.name, color: o.color }))}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="شیوه آشنایی"
              name="source_id"
            >
              <RemoteSelect
                kind="sources"
                placeholder="انتخاب شیوه آشنایی"
                knownOptions={filterOptions.sources.map((o) => ({ value: o.id, label: o.name, color: o.color }))}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  تگ‌ها{' '}
                  <Tooltip title="می‌توانید برای هر معامله یک یا چند تگ مشخص کنید. این تگ‌ها به شما کمک می‌کنند تا معاملات مشابه را گروه‌بندی کرده و به راحتی آن‌ها را مدیریت کنید.">
                    <QuestionCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="tags"
            >
              <RemoteSelect
                kind="tags"
                mode="multiple"
                showColor
                placeholder="انتخاب تگ‌ها"
                knownOptions={filterOptions.tags.map((o) => ({ value: o.id, label: o.name, color: o.color }))}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="توضیحات"
              name="note"
            >
              <Input.TextArea
                rows={3}
                placeholder="توضیحات معامله را وارد کنید"
                style={{ fontFamily: 'CustomFont' }}
                maxLength={2000}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* دکمه‌های عملیات */}
      <div className="flex justify-end gap-4 mt-6">
        <Button
          icon={<CloseOutlined />}
          onClick={onCancel}
          size="middle"
          style={{ fontFamily: 'CustomFont', minWidth: '120px' }}
        >
          انصراف
        </Button>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          htmlType="submit"
          loading={loading}
          size="middle"
          className="bg-green-600 hover:bg-green-700 border-green-600"
          style={{ fontFamily: 'CustomFont', minWidth: '120px' }}
        >
          {isEdit ? 'ذخیره تغییرات' : 'ذخیره'}
        </Button>
      </div>
    </Form>
  );
};

export default DealForm;
